import { useEffect, useRef, useState } from "react";
import { Play, Image as ImageIcon } from "lucide-react";
import { FEED_ITEMS } from "@/lib/data";
import { IOSHighlight } from "@/components/site/iOSKit";

const FeedTile = ({ item, index }) => {
    const ref = useRef(null);
    const [playing, setPlaying] = useState(false);
    const isVideo = item.type === "video";

    // Force first frame to render as thumbnail
    useEffect(() => {
        if (!isVideo) return;
        const el = ref.current;
        if (!el) return;
        const setFrame = () => {
            try {
                el.currentTime = 0.1;
            } catch (e) {}
        };
        el.addEventListener("loadedmetadata", setFrame, { once: true });
        return () => el.removeEventListener("loadedmetadata", setFrame);
    }, [isVideo]);

    const onEnter = () => {
        if (!isVideo) return;
        const el = ref.current;
        if (!el) return;
        el.play().catch(() => {});
        setPlaying(true);
    };
    const onLeave = () => {
        if (!isVideo) return;
        const el = ref.current;
        if (!el) return;
        el.pause();
        el.currentTime = 0;
        setPlaying(false);
    };

    return (
        <div
            data-testid={`feed-tile-${index}`}
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
            className="feed-tile group relative overflow-hidden bg-[#141414] aspect-[9/16] cursor-pointer"
        >
            {isVideo ? (
                <video
                    ref={ref}
                    muted
                    loop
                    playsInline
                    preload="auto"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                >
                    <source src={`${item.src}#t=0.1`} type="video/mp4" />
                </video>
            ) : (
                <img
                    src={item.src}
                    alt={item.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                />
            )}

            {/* Gradient overlay */}
            <div
                className={`absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent transition-opacity duration-500 ${
                    playing ? "opacity-30" : "opacity-90 group-hover:opacity-70"
                }`}
            />

            {/* Type indicator (top-right) — Instagram style */}
            <div className="absolute top-3 right-3 md:top-4 md:right-4">
                <div className="flex items-center gap-1.5 bg-black/40 backdrop-blur-md border border-white/10 rounded-full px-2.5 py-1">
                    {isVideo ? (
                        <Play className="w-3 h-3 text-white fill-white" />
                    ) : (
                        <ImageIcon className="w-3 h-3 text-white" />
                    )}
                    <span className="text-[9px] font-semibold uppercase tracking-wider text-white/90">
                        {isVideo ? "Reel" : "Post"}
                    </span>
                </div>
            </div>

            {/* Center play for videos */}
            {isVideo && (
                <div
                    className={`absolute inset-0 flex items-center justify-center transition-all duration-500 ${
                        playing
                            ? "opacity-0 scale-90"
                            : "opacity-0 group-hover:opacity-100 group-hover:scale-100 scale-90"
                    }`}
                >
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#FF2A2A]/90 backdrop-blur flex items-center justify-center border border-white/20 shadow-[0_0_40px_rgba(255,42,42,0.5)]">
                        <Play className="w-5 h-5 md:w-6 md:h-6 text-white fill-white ml-0.5" />
                    </div>
                </div>
            )}

            {/* Bottom caption — slides in on hover */}
            <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5 transition-all duration-500 translate-y-2 group-hover:translate-y-0 opacity-90 group-hover:opacity-100">
                <span className="text-[9px] md:text-[10px] tracking-[0.25em] uppercase text-[#FF2A2A] font-bold">
                    {item.tag}
                </span>
                <h3 className="font-display text-sm md:text-lg leading-tight text-white mt-0.5">
                    {item.title}
                </h3>
            </div>

            {/* Index number */}
            <div className="absolute top-3 left-3 md:top-4 md:left-4 font-mono text-[10px] text-white/50">
                {String(index + 1).padStart(2, "0")}
            </div>
        </div>
    );
};

export const VideoReel = () => {
    return (
        <section
            id="reel"
            data-testid="reel-section"
            className="relative py-24 md:py-32 bg-[#0A0A0A]"
        >
            {/* Header */}
            <div className="px-6 md:px-12 lg:px-20 mb-14 md:mb-16">
                <div className="flex items-end justify-between gap-6 flex-wrap">
                    <div>
                        <div className="flex items-center gap-3 mb-5">
                            <span className="w-10 h-px bg-[#FF2A2A]" />
                            <span className="text-[11px] tracking-[0.3em] uppercase text-white/60">
                                @Impacto__Visual
                            </span>
                        </div>
                        <h2
                            data-testid="reel-heading"
                            className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-balance max-w-3xl"
                        >
                            <span className="font-serif text-white/90">Trabajo</span>{" "}
                            que <IOSHighlight variant="red">habla</IOSHighlight>
                            <br />
                            por nosotros.
                        </h2>
                    </div>
                    <p className="text-white/60 max-w-xs text-sm md:text-base leading-relaxed">
                        Un feed como lo vives en Instagram. Pasa el cursor
                        sobre cualquier Reel para verlo en movimiento.
                    </p>
                </div>
            </div>

            {/* Instagram-style feed grid — 3 columns, no gaps, square tiles */}
            <div
                data-testid="feed-grid"
                className="grid grid-cols-3 gap-0 w-full"
            >
                {FEED_ITEMS.map((item, i) => (
                    <FeedTile key={item.id} item={item} index={i} />
                ))}
            </div>

            {/* Footer strip */}
            <div className="px-6 md:px-12 lg:px-20 mt-14">
                <div className="flex items-center justify-between border-t border-white/10 pt-6 text-xs tracking-[0.25em] uppercase text-white/50 gap-4 flex-wrap">
                    <span>Publicidad Impresa y Producción · Publicidad Digital y Branding</span>
                    <a
                        href="#contacto"
                        data-testid="reel-contact-link"
                        className="hover:text-[#FF2A2A] transition-colors"
                    >
                        Tu marca acá →
                    </a>
                </div>
            </div>
        </section>
    );
};
